import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Copy, Download, Check, X, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { auth } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Mandatory 2FA Setup Page
 * 
 * This page is shown after registration when 2FA is required.
 * Users cannot skip this step - they must complete 2FA setup to continue.
 */
const Mandatory2FASetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: Info, 2: QR Code, 3: Verify, 4: Backup Codes
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Check if user is logged in or has token from OAuth
  useEffect(() => {
    // Check for token in URL (from Microsoft OAuth)
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      localStorage.setItem('token', tokenFromUrl);
      // Fetch user info
      fetchUserInfo(tokenFromUrl);
    }
    
    const user = auth.getUser();
    const token = auth.getToken();
    
    if (!user && !token && !tokenFromUrl) {
      navigate('/lexy/login');
    }
  }, [navigate, searchParams]);
  
  const fetchUserInfo = async (token) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        localStorage.setItem('user', JSON.stringify(response.data));
      }
    } catch (err) {
      console.error('Failed to fetch user:', err);
    }
  };

  const handleEnable2FA = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/2fa/enable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setBackupCodes(response.data.backup_codes);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke aktivere 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    if (verificationCode.length !== 6) {
      setError('Koden må være 6 sifre');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/auth/2fa/verify-setup`,
        { code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ugyldig kode');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleDownloadCodes = () => {
    const text = `LEXY 2FA BACKUP-KODER
Generert: ${new Date().toLocaleString('no-NO')}

⚠️ VIKTIG: Oppbevar disse kodene på et trygt sted!
Hver kode kan kun brukes én gang.

${backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

Hvis du mister tilgang til autentiseringsappen din, kan du bruke
disse kodene for å logge inn.
`;
    
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lexy-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleComplete = () => {
    // Update user data to reflect 2FA completion
    const user = auth.getUser();
    if (user) {
      user.two_factor_enabled = true;
      user.two_factor_setup_complete = true;
      auth.saveUser(user);
    }
    
    // Navigate to dashboard
    navigate('/lexy/dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Security Notice */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-amber-400 font-semibold mb-1">Sikkerhet er påkrevd</h3>
              <p className="text-amber-200/80 text-sm">
                For å beskytte din konto og klientdata, må du sette opp to-faktor autentisering 
                før du kan bruke Lexy. Dette er obligatorisk for alle brukere.
              </p>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-3 rounded-xl">
                <Shield className="w-7 h-7" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Sett opp To-faktor Autentisering</h2>
                <p className="text-blue-100 text-sm">Steg {step} av 3</p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="h-1 bg-white/10">
            <div 
              className="h-full bg-blue-500 transition-all duration-500"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>

          {/* Content */}
          <div className="p-6 text-white">
            {/* Step 1: Introduction */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <p className="text-slate-300 mb-6">
                    To-faktor autentisering beskytter kontoen din ved å kreve både passordet ditt 
                    og en 6-sifret kode fra autentiseringsappen din ved innlogging.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Hva du trenger:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="bg-green-500/20 text-green-400 rounded-full p-1 mt-0.5">
                        <Check className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-medium">En autentiseringsapp</p>
                        <p className="text-sm text-slate-400">
                          Google Authenticator, Microsoft Authenticator, eller Authy
                        </p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="bg-green-500/20 text-green-400 rounded-full p-1 mt-0.5">
                        <Check className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="font-medium">Mobiltelefonen din</p>
                        <p className="text-sm text-slate-400">
                          For å skanne QR-koden
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500/50 text-red-200 p-4 rounded-lg">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleEnable2FA}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Forbereder...
                    </>
                  ) : (
                    <>
                      <Shield className="w-5 h-5" />
                      Start oppsett
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Step 2: QR Code + Verification */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-bold mb-2">Skann QR-koden</h3>
                  <p className="text-slate-300">
                    Åpne autentiseringsappen din og skann QR-koden nedenfor
                  </p>
                </div>

                {/* QR Code */}
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-lg">
                    <img src={qrCode} alt="QR Code" className="w-56 h-56" />
                  </div>
                </div>

                {/* Manual Entry */}
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-sm text-slate-400 mb-2">
                    Kan ikke skanne? Skriv inn denne koden manuelt:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-slate-900 p-3 rounded border border-slate-700 text-sm font-mono text-slate-200 break-all">
                      {secret}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(secret);
                        setCopiedIndex('secret');
                        setTimeout(() => setCopiedIndex(null), 2000);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded transition-colors"
                    >
                      {copiedIndex === 'secret' ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <Copy className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Verification Code Input */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Skriv inn 6-sifret kode fra appen
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-full text-center text-2xl font-mono tracking-widest p-4 bg-slate-900 border-2 border-slate-700 rounded-lg focus:border-blue-500 focus:outline-none text-white"
                    maxLength={6}
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500/50 text-red-200 p-4 rounded-lg">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleVerifySetup}
                  disabled={loading || verificationCode.length !== 6}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verifiserer...
                    </>
                  ) : (
                    'Bekreft og aktiver'
                  )}
                </button>
              </div>
            )}

            {/* Step 3: Backup Codes */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="bg-green-500/20 text-green-400 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <Check className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">2FA er aktivert! 🎉</h3>
                  <p className="text-slate-300">
                    Lagre backup-kodene på et trygt sted før du fortsetter
                  </p>
                </div>

                <div className="bg-amber-500/20 border border-amber-500/30 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className="font-semibold text-amber-200">Viktig!</p>
                      <p className="text-sm text-amber-100/80">
                        Hver backup-kode kan kun brukes én gang. Hvis du mister tilgang til
                        autentiseringsappen din, kan du bruke disse kodene for å logge inn.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Backup Codes Grid */}
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                  <div className="grid grid-cols-2 gap-3">
                    {backupCodes.map((code, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-slate-900 p-3 rounded border border-slate-700"
                      >
                        <code className="font-mono text-sm text-slate-200">{code}</code>
                        <button
                          onClick={() => handleCopyCode(code, index)}
                          className="text-blue-400 hover:text-blue-300 ml-2"
                        >
                          {copiedIndex === index ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={handleDownloadCodes}
                    className="flex-1 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Last ned koder
                  </button>
                  <button
                    onClick={handleComplete}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  >
                    Fortsett til Lexy
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Mandatory2FASetup;
